import allImgPaths from "assets/images";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ClaimElement from "./ClaimElement";
import ClauseFeatureStrip from "./ClauseFeatureStrip";
import ClaimGraphics from "./ClaimGraphics";
import ClaimStructure from "./ClaimStructure";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAppState from "context/useAppState";

interface ClaimDataProps {
  type: string;
  APIData: any;
  filteredClaimsData: any;
  activeIndex: any;
  handleClaimClick: any;
  claimSelectionLoader: any;
  selectedClaim: any;
  summarizedAPIData?: any;
  selectedSimilarity?: any;
  claimStructureData?: any;
  setClaimStructureData?: any;
  generateSelectedProd?: any;
  selectedSimilarityItems?: any;
  handleSelect?: any;
  toggle?: any;
  setToggle?: any;
  setIsShowSideBarCitations?: any;
  setSelectedCitation?: any;
  clickedHeatmapTargetId?: string;
  /**
   * Expansion state of the covered-product cards saved before navigating to
   * "View More Details", so coming back restores the same open cards.
   */
  initialExpandedTargets?: { [key: string]: boolean };
  /**
   * The citation item whose "View More Details" was clicked, so coming back
   * scrolls to and highlights that exact item.
   */
  restoreScrollTarget?: {
    targetDocumentId: string;
    clickedItemIndex: number;
    isMatchedItem: boolean;
  };
  /**
   * Hands the parent a function that expands the first covered-product target,
   * so the guided walkthrough can reveal the "View More Details" / citation
   * anchors before driving to those steps.
   */
  onRegisterExpandFirstTarget?: (expand: () => void) => void;
}

const ClaimData = ({
  type = "vijilize",
  APIData,
  filteredClaimsData,
  activeIndex,
  handleClaimClick,
  claimSelectionLoader,
  selectedClaim,
  claimStructureData,
  setClaimStructureData,
  summarizedAPIData = null,
  selectedSimilarity = [],
  generateSelectedProd = null,
  selectedSimilarityItems = {},
  handleSelect = null,
  toggle = 0,
  setToggle = null,
  setIsShowSideBarCitations = null,
  setSelectedCitation = null,
  clickedHeatmapTargetId = undefined,
  initialExpandedTargets = undefined,
  restoreScrollTarget = undefined,
  onRegisterExpandFirstTarget = undefined,
}: ClaimDataProps) => {
  const [claimFiguresTotal, setClaimFiguresTotal] = useState<any>(0);
  const [expandedTargets, setExpandedTargets] = useState<{[key: string]: boolean}>({});
  // Per-clause expansion state inside Covered Patents (default collapsed)
  const [expandedClauses, setExpandedClauses] = useState<{[key: string]: boolean}>({});
  const toggleClause = (key: string) =>
    setExpandedClauses((prev) => ({ ...prev, [key]: !prev[key] }));
  // Covered Patents rendering mode: new per-clause Features View vs the
  // original flat matched/unmatched Classic View.
  const [coveredView, setCoveredView] = useState<"features" | "classic">(
    "features"
  );
  // Draggable split between Claim Features and Covered Patents (vijilize only).
  // Grip-chip drags also scroll the panel vertically, scrollbar-thumb style;
  // plain divider drags stay horizontal-only. DOM-first + rAF per the
  // drag-resize recipe — state commits on release.
  const [claimColPct, setClaimColPct] = useState(33.33);
  const startColResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const stripEl = e.currentTarget as HTMLElement;
    const container = stripEl.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const leftCol = container.firstElementChild as HTMLElement | null;
    const fromGrip = !!(e.target as HTMLElement).closest("[data-grip]");
    const scrollEl = stripEl.closest(".hide-scrollbar3") as HTMLElement | null;
    const stickyEl = stripEl.querySelector(
      "[data-grip-sticky]"
    ) as HTMLElement | null;
    const startX = e.clientX;
    const startY = e.clientY;
    const scrollTop0 = scrollEl?.scrollTop ?? 0;
    const chipTop0 =
      stickyEl && scrollEl
        ? stickyEl.getBoundingClientRect().top -
          scrollEl.getBoundingClientRect().top
        : 0;
    const track = scrollEl?.clientHeight ?? 0;
    // Thumb mapping: full drag over the track covers the full scroll range
    const ratio =
      scrollEl && track > 60
        ? Math.max(1, (scrollEl.scrollHeight - track) / (track - 60))
        : 1;
    let raf = 0;
    let cx = startX;
    let cy = startY;
    let pct = claimColPct;
    const apply = () => {
      raf = 0;
      pct = Math.min(65, Math.max(20, ((cx - rect.left) / rect.width) * 100));
      if (leftCol) leftCol.style.width = `${pct}%`;
      if (fromGrip && scrollEl) {
        const dy = cy - startY;
        scrollEl.scrollTop = scrollTop0 + dy * ratio;
        if (stickyEl) {
          stickyEl.style.top = `${Math.min(
            Math.max(track - 60, 8),
            Math.max(8, chipTop0 + dy)
          )}px`;
        }
      }
    };
    const onMove = (ev: MouseEvent) => {
      cx = ev.clientX;
      cy = ev.clientY;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onUp = () => {
      if (raf) cancelAnimationFrame(raf);
      apply();
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setClaimColPct(pct);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = fromGrip ? "move" : "col-resize";
    document.body.style.userSelect = "none";
  };
  // Restore covered-product expansion after returning from "View More Details"
  React.useEffect(() => {
    if (initialExpandedTargets && Object.keys(initialExpandedTargets).length) {
      setExpandedTargets(initialExpandedTargets);
    }
  }, [initialExpandedTargets]);

  // Scroll back to the exact citation item whose "View More Details" was
  // clicked. Waits a tick so the restored expansion state has rendered the
  // item first.
  React.useEffect(() => {
    if (!restoreScrollTarget?.targetDocumentId) return;
    const { targetDocumentId, clickedItemIndex, isMatchedItem } =
      restoreScrollTarget;
    const elId = `citation-${targetDocumentId}-${
      isMatchedItem ? "matched" : "unmatched"
    }-${clickedItemIndex}`;
    const scrollTimer = setTimeout(() => {
      const el = document.getElementById(elId);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
    return () => clearTimeout(scrollTimer);
  }, [restoreScrollTarget]);

  // When similarity results load, auto-expand the heatmap-clicked target only.
  // Skipped when restoring saved expansion state after "View More Details".
  React.useEffect(() => {
    if (initialExpandedTargets && Object.keys(initialExpandedTargets).length) return;
    if (!clickedHeatmapTargetId || !selectedSimilarity?.length) return;
    const sorted = [...selectedSimilarity].sort((a: any, b: any) => b.score - a.score);
    const newState: {[key: string]: boolean} = {};
    sorted.forEach((item: any, index: number) => {
      const key = item.targetDocumentId + index;
      newState[key] = item.targetDocumentId === clickedHeatmapTargetId;
    });
    setExpandedTargets(newState);
  }, [clickedHeatmapTargetId, selectedSimilarity, initialExpandedTargets]);

  const toggleTargetExpand = (targetId: string) => {
    setExpandedTargets((prev) => ({
      ...prev,
      [targetId]: !prev[targetId],
    }));
  };

  const isTargetExpanded = (targetId: string) => {
    // Default to collapsed (false) if not set
    return expandedTargets[targetId] === true;
  };

  // Expose a "expand the first covered-product target" trigger to the parent so
  // the guided walkthrough can reveal the View More / citation anchors. The key
  // mirrors the one used when rendering targets: `${targetDocumentId}${index}`.
  const expandFirstTarget = React.useCallback(() => {
    if (!selectedSimilarity?.length) return;
    const sorted = [...selectedSimilarity].sort(
      (a: any, b: any) => b.score - a.score
    );
    const firstKey = sorted[0].targetDocumentId + 0;
    setExpandedTargets((prev) => ({ ...prev, [firstKey]: true }));
  }, [selectedSimilarity]);

  React.useEffect(() => {
    onRegisterExpandFirstTarget?.(expandFirstTarget);
  }, [onRegisterExpandFirstTarget, expandFirstTarget]);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setMarkdownContent,
    setImageCoordinates,
    setOriginalImagePath,
    setMarkdownS3Path,
    setAnnotationData,
  } = useAppState("vijilize");

  const generateMaxPercentageData: any = (patentId: any, claimKey: any) => {
    const summarizedData: any =
      summarizedAPIData &&
      summarizedAPIData.find((item: any) => item[patentId]);

    const summarizedDataValues: any =
      summarizedData && Object.values(summarizedData)[0];

    const claimData: any =
      summarizedDataValues && summarizedDataValues[claimKey];

    const maxScore = claimData
      ? claimData.sort((a: any, b: any) => b.score - a.score)[0].score
      : null;
    return maxScore;
  };

  const openCitations = (data: any) => {
    const citation = { value: data, label: `${data}` };
    setSelectedCitation(citation);
    setIsShowSideBarCitations(true);
  };

  const cleanText = (text: string) => {
    return text.replace(/\\/g, ""); // Removes all backslashes
  };

  const convertToSuperscriptAndHyperlink = (text: any) => {
    text = cleanText(text);

    const regex = /\[\[(\d+(?:\s*,\s*\d+)*)\]\]|\[(\d+(?:\s*,\s*\d+)*)\]/g;
    const parts: any[] = [];
    let lastIndex = 0;

    text.replace(
      regex,
      (match: any, doubleGroup: any, singleGroup: any, index: any) => {
        parts.push(text.slice(lastIndex, index));

        const groupNumbers = (doubleGroup || singleGroup)
          .split(",")
          .map((num: any) => num.trim())
          .sort((a: any, b: any) => Number(a) - Number(b));

        groupNumbers.forEach((num: any, idx: number) => {
          parts.push({ type: "link", number: num });
          if (idx < groupNumbers.length - 1) {
            parts.push({ type: "sup-comma", value: "," });
          }
        });

        lastIndex = index + match.length;
      }
    );

    parts.push(text.slice(lastIndex));
    return parts;
  };

  // Path format: /project/{projectId}/vijilize/{comparisonId}
  const comparisonId =
    location.pathname.match(/\/vijilize\/([^\/]+)/)?.[1] || null;

  // Navigate to the View More Details page for one covered-product item.
  // Shared by the citation text list and the Features View strips.
  const openViewMoreDetails = (
    itemObj: any,
    itemIndex: number,
    isMatchedItem: boolean,
    parentTargetDocumentId?: string,
    preferredView?: "graph" | "classic"
  ) => {
    // Slim payloads carry no markdown_content — the view-more page fetches
    // it by annotation_id instead.
    if (!itemObj?.markdown_content && !itemObj?.annotation_id) return;
    const patentId = activeIndex?.patentId;
    let claimKey = `claim${activeIndex?.claimIndex + 1}`;

    const patentData = filteredClaimsData[patentId];
    if (patentData && patentData.claims) {
      const claimKeys = Object.keys(patentData.claims);

      // Proceed only if there's exactly one claim
      if (claimKeys.length === 1) {
        claimKey = claimKeys[0];
      }
    }

    setAnnotationData({
      patentId,
      claimKey,
      annotationId: itemObj?.annotation_id,
      isMatchedItem,
    });
    // Absent on slim items — MarkdownViewer self-fetches via annotation_id
    setMarkdownContent(itemObj.markdown_content || "");
    setImageCoordinates(itemObj.image_coordinates || null);
    setOriginalImagePath(itemObj.original_image_path || "");
    setMarkdownS3Path(itemObj.markdown_image?.[0] || "");
    const currentPath = location.pathname;

    const claimData = {
      activeIndex,
      selectedClaim,
      selectedSimilarity,
      clickedItemIndex: itemIndex,
      isMatchedItem,
      annotationId: itemObj?.annotation_id,
      storedClaimKey: claimKey,
      comparisonId: comparisonId,
      targetDocumentId: parentTargetDocumentId || null,
      sourcePatentId: patentId || null,
      expandedTargets,
      preferredDetailsView: preferredView || "graph",
      // Analyzer-built element profiles — the chart's figure modal shows the
      // clicked feature's profile, same as the Claim Features panel.
      patentDict: filteredClaimsData[patentId]?.patent_dict || null,
    };
    sessionStorage.setItem("vijilizeClaimData", JSON.stringify(claimData));
    navigate(`${currentPath}/view-more`);
  };

  const renderCitationItem = (
    itemText: any,
    itemIndex: number,
    isMatchedItem: boolean,
    parentTargetDocumentId?: string
  ) => {
    const isObjectFormat =
      typeof itemText === "object" && itemText !== null && "text" in itemText;

    let textContent = "";
    let hexColor: any = null;
    let chartUrl: any = null;
    let subFeature: any = null;

    if (isObjectFormat) {
      textContent = itemText.text;
      hexColor = itemText.element_hex_code || null;
      subFeature = itemText.sub_feature || null;
    } else {
      const chartMatch = itemText.match(
        /##DETAILED_CLAIM_ELEMENT_CHART:(.*?)##/
      );
      const hexMatch = itemText.match(/##ELEMENT_HEX_CODE:(#[0-9A-Fa-f]{6})/);

      textContent = itemText
        .replace(/##DETAILED_CLAIM_ELEMENT_CHART:.*?$/, "")
        .replace(/##ELEMENT_HEX_CODE:(#[0-9A-Fa-f]{6})/, "")
        .trim();

      chartUrl = chartMatch ? chartMatch[1] : null;
      hexColor = hexMatch ? hexMatch[1] : null;
    }

    const parsedText = convertToSuperscriptAndHyperlink(textContent);

    const content = parsedText.map((part, index) => {
      if (typeof part === "string") {
        return hexColor ? (
          <span key={index} style={{ color: hexColor }}>
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        );
      } else if (part.type === "link") {
        return (
          <span
            key={index}
            className="walkthrough-citation-sup text-blue-600 hover:underline cursor-pointer"
            style={hexColor ? { color: hexColor } : {}}
            onClick={() => openCitations(part.number)}
          >
            <sup>{part.number}</sup>
          </span>
        );
      } else if (part.type === "sup-comma") {
        return (
          <span key={index} style={hexColor ? { color: hexColor } : {}}>
            <sup>{part.value}</sup>
          </span>
        );
      }
      return null;
    });

    const handleViewMore = () => {
      if (isObjectFormat) {
        openViewMoreDetails(
          itemText,
          itemIndex,
          isMatchedItem,
          parentTargetDocumentId
        );
      } else if (chartUrl) {
        window.open(chartUrl, "_blank");
      }
    };

    const showButton =
      chartUrl ||
      (isObjectFormat && (itemText.markdown_content || itemText.has_markdown));

    return (
      <>
        {subFeature && (
          <h4
            className="text-lg font-interSemiBold mb-2"
            style={{ color: hexColor }}
          >
            {subFeature}
          </h4>
        )}
        <span>{content}</span>
        {isObjectFormat &&
          itemText?.is_design_patent &&
          itemText?.markdown_image &&
          itemText?.markdown_image[0] && (
            <div className="mt-3 mb-2">
              <img
                src={itemText.markdown_image[0]}
                alt="Design Patent"
                className="max-w-full h-auto rounded-md max-h-[300px] object-contain"
              />
            </div>
          )}
        {showButton && (
          <div className="mt-2">
            <button
              className="walkthrough-view-more text-sm text-[#1751D0] font-interSemiBold"
              onClick={handleViewMore}
            >
              {`View More Details`}
            </button>
          </div>
        )}
      </>
    );
  };

  // ----- Clause grouping for Covered Patents -----
  const clauseNorm = (s: any) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const clauseNameNorm = (s: any) =>
    clauseNorm(String(s || "").replace(/\([^)]*\)/g, " "));
  const clausePartNums = (s: any): string[] => {
    const nums: string[] = [];
    String(s || "").replace(/\(([^)]*)\)/g, (_m: string, inner: string) => {
      inner.split(",").forEach((n) => {
        const t = n.trim();
        if (/^\d+$/.test(t)) nums.push(t);
      });
      return "";
    });
    return nums;
  };

  // Preamble + each element = one clause; parts keep their per-feature colors.
  const buildClauseGroups = (claim: any) => {
    const groups: { parts: any[] }[] = [];
    const pre = claim?.claim_preamble || claim?.preamble;
    if (Array.isArray(pre)) groups.push({ parts: pre });
    else if (pre)
      groups.push({ parts: [{ description: pre, hex_code: claim?.hex_code }] });
    (claim?.elements || []).forEach((el: any) => {
      groups.push({ parts: el?.features?.length ? el.features : [el] });
    });
    return groups;
  };

  // Part number first, then numeral-stripped name, then hex — hex last, the
  // color palette repeats across elements.
  const clauseIndexForItem = (item: any, groups: { parts: any[] }[]) => {
    const isObj = typeof item === "object" && item !== null;
    const sub = isObj ? item.sub_feature : null;
    const hex = isObj
      ? item.element_hex_code
      : (String(item).match(/##ELEMENT_HEX_CODE:(#[0-9A-Fa-f]{6})/) || [])[1];
    const subNums = clausePartNums(sub);
    if (subNums.length) {
      const byNum = groups.findIndex((g) =>
        g.parts.some((p) =>
          clausePartNums(p?.description).some((n) => subNums.includes(n))
        )
      );
      if (byNum >= 0) return byNum;
    }
    const name = clauseNameNorm(sub);
    if (name) {
      const byName = groups.findIndex((g) =>
        g.parts.some((p) => clauseNameNorm(p?.description) === name)
      );
      if (byName >= 0) return byName;
    }
    if (hex) {
      const byHex = groups.findIndex((g) =>
        g.parts.some((p) => p?.hex_code === hex)
      );
      if (byHex >= 0) return byHex;
    }
    return -1;
  };

  return (
    <>
      {Object.keys(filteredClaimsData).length > 0 && (
        <div className="overflow-y-scroll hide-scrollbar3 h-[calc(100vh_-_166px)] bg-[#fff]">
          {Object.entries(filteredClaimsData).map(([patentId, patent]: any) => (
            // loop
            <div className="mb-2 last:mb-0 bg-white px-3 pb-2" key={patentId}>
              {/* Claim Info */}
              <div className="py-3">
                <div className="pb-2">
                  <h3 className="text-base font-interSemiBold text-[#0A090B] mb-0.5">
                    {patent.patentNumber}
                  </h3>
                  {/* <p className='text-sm font-interRegular text-[#7F7D83]'>Cover and grip for handheld devices</p> */}
                </div>
                <div className="flex items-center gap-x-4">
                  <span className="text-sm font-interMedium text-[#000000]">
                    {Object.keys(patent.claims).length} matching claims found
                  </span>
                  {/* <span className='text-sm font-interMedium text-[#1A75FF] cursor-pointer hover:text-[#113B98]'>Expand All</span> */}
                </div>
              </div>

              {Object.entries(patent.claims).map(
                ([claimKey, claim], claimIndex) => (
                  <div
                    key={patentId + "_" + claimIndex}
                    id={patentId + "_" + claimIndex}
                    className={`mb-3 last:mb-2 ${activeIndex.patentId === patentId &&
                      activeIndex.claimIndex === claimIndex
                      ? "active-accordion"
                      : "accordion-hide"
                      }`}
                  >
                    <div
                      className={`border border-[#CCE0FF] px-5 py-5 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between`}
                      onClick={(event) =>
                        handleClaimClick(event, patent, claimIndex, claimKey)
                      }
                    >
                      <div className="flex items-center gap-x-3">
                        <span className="font-interSemiBold text-base text-[#101828] flex items-center gap-x-2">
                          Claim {claimKey.replace("claim", "")}
                        </span>

                        {type !== "analysis" &&
                          (() => {
                            const percentageData = generateMaxPercentageData(
                              patentId,
                              claimKey
                            );
                            return typeof percentageData === "number" &&
                              percentageData >= 0 ? (
                              <>
                                <span className="px-1.5 py-0.5 rounded-md bg-[#019939] text-white font-interRegular text-sm">
                                  {percentageData}% Match
                                </span>
                                <span>Top Match</span>
                              </>
                            ) : null;
                          })()}
                      </div>

                      <div className="flex items-center gap-x-3.5">
                        {type === "vijilize" && (
                          <>
                            {generateSelectedProd(claimKey, patentId) !== 0 && (
                              <span className="text-sm text-[#344051] font-interSemiBold">
                                {" "}
                                {generateSelectedProd(claimKey, patentId)}{" "}
                                Selected{" "}
                              </span>
                            )}

                            <span
                              id="viewCitations"
                              className="text-sm text-[#1751D0] font-interSemiBold"
                            >
                              View Citations
                            </span>
                          </>
                        )}
                        <img
                          className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded"
                          src={allImgPaths.dropdownArrow}
                          alt=""
                        />
                      </div>
                    </div>

                    {activeIndex.patentId === patentId &&
                      activeIndex.claimIndex === claimIndex && (
                        <div className={`accordion-content`}>
                          <div className="flex">
                            {/* First */}
                            <div
                              className={`${type === "analysis" ? "w-1/2" : "flex-shrink-0"
                                } border border-[#CED2DA] border-t-0 rounded-bl-lg`}
                              style={
                                type !== "analysis"
                                  ? { width: `${claimColPct}%` }
                                  : undefined
                              }
                            >
                              <ClaimElement
                                claim={claim}
                                type={"vijilize"}
                                patentDict={patent?.patent_dict}
                              />
                            </div>

                            {/* Drag handle — resizes the two-column split */}
                            {type !== "analysis" && (
                              <div
                                onMouseDown={startColResize}
                                className="relative w-1.5 flex-shrink-0 cursor-col-resize group hover:bg-[#E8F0FE] active:bg-[#E8F0FE]"
                                title="Drag to resize"
                              >
                                {/* Grip sticks mid-viewport; dragging it vertically scrolls the panel */}
                                <div data-grip-sticky className="sticky top-[45vh] z-10">
                                  <div
                                    data-grip
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border border-[#CED2DA] rounded-md py-1 shadow-sm text-[#98A2B3] group-hover:border-[#1751D0] group-hover:text-[#1751D0] flex items-center cursor-move"
                                    title="Drag sideways to resize · up/down to scroll"
                                  >
                                    <DragIndicatorIcon sx={{ fontSize: 14 }} />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Middle — Claim Figures (analysis view only) */}
                            {type === "analysis" && (
                            <div
                              className={`w-1/2 border border-[#CED2DA] border-t-0`}
                            >
                              {claimSelectionLoader && (
                                <div className="border rounded-lg p-4 animate-pulse">
                                  <div className="flex justify-center mb-4">
                                    <div className="flex">
                                      <div className="w-32 h-10 bg-gray-200 rounded-l"></div>
                                      <div className="w-32 h-10 bg-gray-200 rounded-r"></div>
                                    </div>
                                  </div>
                                  <div className="space-y-4">
                                    <div className="h-48 bg-gray-200 rounded"></div>
                                    <div className="h-48 bg-gray-200 rounded"></div>
                                  </div>
                                </div>
                              )}
                              {!claimSelectionLoader && (
                                <div className="">
                                  <div className="px-4 py-3 border-[#CED2DA] border-b">
                                    <p className="text-base font-interSemiBold text-[#0A090B]">
                                      Claim Figures
                                    </p>
                                    <p className="text-xs font-interMedium leading-[1.5]">
                                      {claimFiguresTotal} found
                                    </p>
                                  </div>

                                  <div className="py-4">
                                    {type === "analysis" && (
                                      <>
                                        <ul className="flex items-center justify-center pb-4">
                                          <li
                                            onClick={(e) => {
                                              setToggle(0);
                                            }}
                                            className={`font-interSemiBold text-base py-2.5 px-4 border border-[#E6E6E6] text-[#4F4D55] rounded-tl-lg rounded-bl-lg flex items-center gap-x-2 cursor-pointer ${toggle === 0
                                              ? "bg-[#F1F1F1] pointer-none"
                                              : "bg-[#fff]"
                                              }`}
                                          >
                                            <span className="text-sm font-interMedium text-[#4F4D55]">
                                              Claim Graphics
                                            </span>
                                          </li>
                                          {APIData?.structure && (
                                            <li
                                              onClick={(e) => {
                                                setToggle(1);
                                                setClaimStructureData(
                                                  APIData?.structure
                                                );
                                              }}
                                              className={`font-interSemiBold text-base py-2.5 px-4 border border-[#E6E6E6] text-[#4F4D55] rounded-tr-lg rounded-br-lg flex items-center gap-x-2 cursor-pointer ${toggle === 1
                                                ? "bg-[#F1F1F1] pointer-none"
                                                : "bg-[#fff]"
                                                }`}
                                            >
                                              <span className="text-sm font-interMedium text-[#4F4D55]">
                                                Claim Structure
                                              </span>
                                            </li>
                                          )}
                                        </ul>
                                      </>
                                    )}
                                    {toggle === 0 && (
                                      <div className="">
                                        <div className="">
                                          <ClaimGraphics
                                            claim={selectedClaim}
                                            setClaimFiguresTotal={
                                              setClaimFiguresTotal
                                            }
                                          />
                                        </div>
                                      </div>
                                    )}
                                    {toggle === 1 && (
                                      <div className="claim-strcture-wrapper">
                                        <ClaimStructure
                                          data={claimStructureData}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            )}

                            {/* Last */}
                            {type !== "analysis" && (
                              <>
                                <div className="flex-1 min-w-0 border border-[#CED2DA] border-t-0 border-l-0 rounded-br-lg">
                                  <div className="px-4 border-b border-[#CED2DA] relative flex items-center justify-end h-[66px]">
                                    {/* Title centered in the column; toggle stays right */}
                                    <div className="absolute left-1/2 -translate-x-1/2 text-center">
                                      <p className="text-base font-interSemiBold text-[#0A090B]">
                                        Covered Patents
                                      </p>
                                      {selectedSimilarity && (
                                        <p className="text-xs font-interMedium leading-[1.5]">
                                          {selectedSimilarity?.length} matches
                                          found
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-shrink-0 relative">
                                      <button
                                        type="button"
                                        onClick={() => setCoveredView("classic")}
                                        className={`text-xs font-interSemiBold px-3 py-1.5 border border-[#E6E6E6] rounded-tl-lg rounded-bl-lg ${
                                          coveredView === "classic"
                                            ? "bg-[#F5F8FD] text-[#1751D0] border-[#1751D0]"
                                            : "bg-white text-[#4F4D55]"
                                        }`}
                                      >
                                        Elements View
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setCoveredView("features")}
                                        className={`text-xs font-interSemiBold px-3 py-1.5 border border-l-0 border-[#E6E6E6] rounded-tr-lg rounded-br-lg ${
                                          coveredView === "features"
                                            ? "bg-[#F5F8FD] text-[#1751D0] border-[#1751D0] border-l"
                                            : "bg-white text-[#4F4D55]"
                                        }`}
                                      >
                                        Clause View
                                      </button>
                                    </div>
                                  </div>
                                  <div className="">
                                    {claimSelectionLoader ? (
                                      <div className="border rounded-lg p-4 m-3">
                                        {/* Spinner always at viewport center, same spot as page loads */}
                                        <div className="fixed inset-0 z-10 flex items-center justify-center pointer-events-none">
                                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                        </div>
                                        <div className="space-y-4 animate-pulse">
                                          <div className="h-48 bg-gray-200 rounded"></div>
                                          <div className="h-48 bg-gray-200 rounded"></div>
                                        </div>
                                      </div>
                                    ) : selectedSimilarity &&
                                      selectedSimilarity.length > 0 ? (
                                      [...selectedSimilarity].sort((a: any, b: any) => b.score - a.score).map(
                                        (item: any, index: any) => (
                                          <div
                                            key={item.targetDocumentId + index}
                                            className="mb-4 last:mb-0"
                                          >
                                            <div className="">
                                              <div className="px-3 py-3 relative">
                                                <div className="bg-[#EDEDED] pb-3 pt-3 px-2 mb-4 rounded-md">
                                                  <div className="relative flex items-center gap-x-2 mb-3">
                                                    {type === "vijilize" && (
                                                      <>
                                                        <input
                                                          type="checkbox"
                                                          id={
                                                            "claim" +
                                                            ((activeIndex.claimIndex ||
                                                              0) +
                                                              1) +
                                                            "cid" +
                                                            item.patentId +
                                                            "_id" +
                                                            item.targetDocumentId +
                                                            "check"
                                                          }
                                                          name="checkbox-group"
                                                          checked={
                                                            selectedSimilarityItems[
                                                            `claim${(activeIndex.claimIndex ||
                                                              0) + 1
                                                            }cid${item.patentId
                                                            }_id${item.targetDocumentId
                                                            }check`
                                                            ] || false
                                                          }
                                                          onChange={(e) =>
                                                            handleSelect(
                                                              e,
                                                              item
                                                            )
                                                          }
                                                          onClick={(e) =>
                                                            e.stopPropagation()
                                                          }
                                                          className="bg-white peer appearance-none h-5 w-5 border border-gray-300 rounded-md 
                                                            checked:bg-[#1751D0] hover:border-[#1751D0] checked:border-0 
                                                            transition-colors cursor-pointer flex-shrink-0"
                                                        />
                                                        <svg
                                                          className="absolute w-5 h-5 pointer-events-none hidden peer-checked:block inset-0 stroke-white flex-shrink-0"
                                                          viewBox="0 0 16 16"
                                                          fill="none"
                                                          stroke="currentColor"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        >
                                                          <polyline points="4 8 7 11 12 5"></polyline>
                                                        </svg>
                                                      </>
                                                    )}
                                                    <a
                                                      href={
                                                        item.targetDocumentIdUrl || item.targetDocumentId
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className="cursor-pointer flex-1 min-w-0"
                                                    >
                                                      <p
                                                        title={
                                                          item.targetDocumentId
                                                        }
                                                        className="text-sm truncate hover:text-[#113B98] text-[#1751D0] underline"
                                                      >
                                                        {item.targetDocumentId}
                                                      </p>
                                                    </a>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleTargetExpand(item.targetDocumentId + index);
                                                      }}
                                                      className="walkthrough-expand-target p-1 hover:bg-[#D1D1DB] rounded transition-colors flex-shrink-0"
                                                      title={isTargetExpanded(item.targetDocumentId + index) ? "Collapse" : "Expand"}
                                                    >
                                                      <svg
                                                        className={`w-4 h-4 text-[#4F4D55] transition-transform duration-200 ${
                                                          isTargetExpanded(item.targetDocumentId + index) ? "rotate-180" : ""
                                                        }`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          strokeWidth={2}
                                                          d="M19 9l-7 7-7-7"
                                                        />
                                                      </svg>
                                                    </button>
                                                  </div>
                                                  {/* Overlap score always visible below checkbox/target row */}
                                                  <div className="mt-2 mb-1">
                                                    <span className="text-sm text-black font-interMedium inline-block pb-1">
                                                      Overlap score
                                                    </span>
                                                    <div className="flex items-center gap-x-3 justify-between">
                                                      <div className="bg-[#E4E4E7] h-1.5 w-[97%] relative rounded-lg">
                                                        <div
                                                          className="bg-[#005BC4] h-full absolute top-0 left-0 rounded-lg"
                                                          style={{
                                                            width: `${Math.round(item.score)}%`,
                                                          }}
                                                        ></div>
                                                      </div>
                                                      <span className="font-interSemiBold text-black text-sm">
                                                        {Math.round(item.score)}%
                                                      </span>
                                                    </div>
                                                  </div>
                                                  {isTargetExpanded(item.targetDocumentId + index) &&
                                                    coveredView === "classic" && (
                                                  <div>
                                                    {item?.matched_items &&
                                                      item?.matched_items
                                                        .length > 0 && (
                                                        <>
                                                          <div className="bg-white border border-[#D1D1DB] rounded-md p-3 mt-4">
                                                            <p className="text-base font-interSemiBold text-[#4F4D55] mb-3">
                                                              Matched Items
                                                            </p>
                                                            {item.matched_items.map(
                                                              (
                                                                matchedItem: any,
                                                                index: number
                                                              ) => (
                                                                <p
                                                                  key={
                                                                    matchedItem +
                                                                    index
                                                                  }
                                                                  id={`citation-${item.targetDocumentId}-matched-${index}`}
                                                                  className="text-sm text-[#71717A] mb-3"
                                                                >
                                                                  {renderCitationItem(
                                                                    matchedItem,
                                                                    index,
                                                                    true,
                                                                    item.targetDocumentId
                                                                  )}
                                                                </p>
                                                              )
                                                            )}
                                                          </div>
                                                          {item?.non_matched_items &&
                                                            item
                                                              ?.non_matched_items
                                                              .length > 0 && (
                                                              <hr className="border-t border-[#D1D1DB] my-3" />
                                                            )}
                                                        </>
                                                      )}

                                                    {item?.non_matched_items &&
                                                      item?.non_matched_items
                                                        .length > 0 && (
                                                        <div className="bg-white border border-[#D1D1DB] rounded-md p-3 mt-4">
                                                          <p className="text-base font-interSemiBold text-[#4F4D55] mb-3">
                                                            Unmatched Items
                                                          </p>
                                                          {item.non_matched_items.map(
                                                            (
                                                              unmatchedItem: any,
                                                              index: number
                                                            ) => (
                                                              <p
                                                                key={
                                                                  unmatchedItem +
                                                                  index
                                                                }
                                                                id={`citation-${item.targetDocumentId}-unmatched-${index}`}
                                                                className="text-sm text-[#71717A] mb-3"
                                                              >
                                                                {renderCitationItem(
                                                                  unmatchedItem,
                                                                  index,
                                                                  false,
                                                                  item.targetDocumentId
                                                                )}
                                                              </p>
                                                            )
                                                          )}
                                                        </div>
                                                      )}
                                                  </div>
                                                  )}
                                                  {isTargetExpanded(item.targetDocumentId + index) &&
                                                    coveredView === "features" && (
                                                  <div>
                                                    {(() => {
                                                      // Bucket matched/unmatched items under their claim clause
                                                      const groups = buildClauseGroups(claim);
                                                      const buckets = groups.map(() => ({
                                                        matched: [] as { it: any; idx: number }[],
                                                        unmatched: [] as { it: any; idx: number }[],
                                                      }));
                                                      const other = {
                                                        matched: [] as { it: any; idx: number }[],
                                                        unmatched: [] as { it: any; idx: number }[],
                                                      };
                                                      (item?.matched_items || []).forEach((it: any, idx: number) => {
                                                        const gi = clauseIndexForItem(it, groups);
                                                        (gi >= 0 ? buckets[gi] : other).matched.push({ it, idx });
                                                      });
                                                      (item?.non_matched_items || []).forEach((it: any, idx: number) => {
                                                        const gi = clauseIndexForItem(it, groups);
                                                        (gi >= 0 ? buckets[gi] : other).unmatched.push({ it, idx });
                                                      });
                                                      const sections = [
                                                        ...groups.map((g, gi) => ({
                                                          key: `${item.targetDocumentId}${index}-clause-${gi}`,
                                                          parts: g.parts,
                                                          ...buckets[gi],
                                                        })),
                                                        {
                                                          key: `${item.targetDocumentId}${index}-clause-other`,
                                                          parts: [{ description: "Other" }],
                                                          isOther: true,
                                                          ...other,
                                                        },
                                                      ].filter((s) => s.matched.length + s.unmatched.length > 0);
                                                      return sections.map((section) => {
                                                        const collapsed = expandedClauses[section.key] !== true;
                                                        return (
                                                          <div
                                                            key={section.key}
                                                            className="bg-white border border-[#D1D1DB] rounded-md mt-4"
                                                          >
                                                            <div
                                                              className="flex items-center justify-between gap-x-3 p-3 cursor-pointer"
                                                              onClick={() => toggleClause(section.key)}
                                                            >
                                                              {/* Clause rendered banner-style: gray glue text, bold colored features */}
                                                              <div className="flex-1 border border-gray-200 rounded-xl bg-[#F8FAFC] px-6 py-3 text-center">
                                                                <p className="text-[13px] text-[#475569] leading-relaxed">
                                                                  "
                                                                  {section.parts.map((p: any, pi: number) => (
                                                                    <span
                                                                      key={pi}
                                                                      className={p?.hex_code ? "font-interSemiBold text-[15px]" : undefined}
                                                                      style={p?.hex_code ? { color: p.hex_code } : undefined}
                                                                    >
                                                                      {p?.description}
                                                                    </span>
                                                                  ))}
                                                                  "
                                                                </p>
                                                              </div>
                                                              <div className="flex items-center gap-x-2 flex-shrink-0 mt-0.5">
                                                                <svg
                                                                  className={`w-4 h-4 text-[#4F4D55] transition-transform duration-200 ${
                                                                    collapsed ? "" : "rotate-180"
                                                                  }`}
                                                                  fill="none"
                                                                  stroke="currentColor"
                                                                  viewBox="0 0 24 24"
                                                                >
                                                                  <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M19 9l-7 7-7-7"
                                                                  />
                                                                </svg>
                                                              </div>
                                                            </div>
                                                            {!collapsed && (
                                                              <div className="px-3 pb-3">
                                                                {/* Inline feature analysis — same columns as View More Details.
                                                                    "Other" items share no clause, so they keep the text list. */}
                                                                {!(section as any).isOther ? (
                                                                  <ClauseFeatureStrip
                                                                    items={[
                                                                      ...section.matched.map(({ it, idx }) => ({ it, idx, isMatched: true })),
                                                                      ...section.unmatched.map(({ it, idx }) => ({ it, idx, isMatched: false })),
                                                                    ]}
                                                                    comparisonId={comparisonId}
                                                                    patentId={activeIndex?.patentId}
                                                                    claimKey={claimKey}
                                                                    claim={claim}
                                                                    patentDict={patent?.patent_dict}
                                                                    targetLink={item.targetDocumentIdUrl || item.targetDocumentId}
                                                                    onViewMore={(it, idx, isMatched, preferredView) =>
                                                                      openViewMoreDetails(it, idx, isMatched, item.targetDocumentId, preferredView)
                                                                    }
                                                                    onCitationClick={openCitations}
                                                                  />
                                                                ) : (
                                                                  <>
                                                                    {section.matched.map(({ it, idx }) => (
                                                                      <p
                                                                        key={"m" + idx}
                                                                        id={`citation-${item.targetDocumentId}-matched-${idx}`}
                                                                        className="text-sm text-[#71717A] mb-3"
                                                                      >
                                                                        {renderCitationItem(
                                                                          it,
                                                                          idx,
                                                                          true,
                                                                          item.targetDocumentId
                                                                        )}
                                                                      </p>
                                                                    ))}
                                                                    {section.unmatched.map(({ it, idx }) => (
                                                                      <p
                                                                        key={"u" + idx}
                                                                        id={`citation-${item.targetDocumentId}-unmatched-${idx}`}
                                                                        className="text-sm text-[#71717A] mb-3"
                                                                      >
                                                                        {renderCitationItem(
                                                                          it,
                                                                          idx,
                                                                          false,
                                                                          item.targetDocumentId
                                                                        )}
                                                                      </p>
                                                                    ))}
                                                                  </>
                                                                )}
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      });
                                                    })()}
                                                  </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <>
                                        <div className="text-center pt-24">
                                          <img
                                            className="mb-2.5 ml-auto mr-auto"
                                            src={
                                              allImgPaths.emptyCoverproductsIcon
                                            }
                                            alt=""
                                          />
                                          <p className="font-interSemiBold text-base text-[#101828] pb-1">
                                            No Covered Products found
                                          </p>
                                          <p className="font-interRegular text-sm text-[#475467] max-w-80 mx-auto">
                                            There are no covered products.
                                            Please try another source or
                                            configure scoring.
                                          </p>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )
              )}
            </div>
          ))}
          {Object.entries(filteredClaimsData).length === 0 && (
            <p>No claims found</p>
          )}
        </div>
      )}
    </>
  );
};

export default ClaimData;
